import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MAX_DAYS_BEFORE,
  MAX_OFFSETS,
  MIN_DAYS_BEFORE,
  addOffset,
  offsetChipLabel,
  removeOffset,
} from "../../lib/reminderSettings";

/**
 * Phase 3C.3 — the organization reminder defaults, on the company settings page.
 *
 * OWNER ONLY. The backend narrows the settings projection so these two fields
 * are simply absent for every non-owner member, and this card renders
 * read-only whenever `canEdit` is false. Neither is the real gate: the save is
 * an ordinary company-settings PATCH, and the database refuses it through the
 * org_update_owner RLS policy for anybody who is not an organization owner.
 *
 * The schedule is a chip list, not a free-text field: every value is an offset
 * in whole days, at most MAX_OFFSETS of them, each unique. The database CHECK
 * says the same thing and is what actually decides.
 *
 * Changing these values affects quotations created AFTERWARDS and nothing else.
 * The help text says so, because a user who expects a retroactive change would
 * otherwise be quietly wrong.
 */
export default function ReminderScheduleCard({
  enabled,
  daysBefore,
  canEdit,
  error,
  onChange,
}) {
  const { t } = useTranslation();
  const [draftOffset, setDraftOffset] = useState("");
  const [addError, setAddError] = useState("");
  const offsets = Array.isArray(daysBefore) ? daysBefore : [];

  const add = () => {
    const result = addOffset(offsets, draftOffset);
    if (result.error) {
      setAddError(result.error);
      return;
    }
    setAddError("");
    setDraftOffset("");
    onChange({ enabled, daysBefore: result.list });
  };

  const drop = (days) => {
    setAddError("");
    onChange({ enabled, daysBefore: removeOffset(offsets, days) });
  };

  const full = offsets.length >= MAX_OFFSETS;

  return (
    <div className="qw-card" data-testid="company-reminders-section">
      <div style={{ fontWeight: 600, marginBottom: 12 }}>
        {t("reminders.settings_section")}
      </div>

      <div className="qw-field">
        <label
          htmlFor="company-reminders-enabled"
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: canEdit ? "pointer" : "default" }}
        >
          <input
            id="company-reminders-enabled"
            type="checkbox"
            checked={!!enabled}
            disabled={!canEdit}
            data-testid="company-reminders-enabled"
            onChange={(e) => onChange({ enabled: e.target.checked, daysBefore: offsets })}
          />
          <span>{t("reminders.settings_enabled_label")}</span>
        </label>
        <div className="qw-help" id="company-reminders-enabled-help">
          {t("reminders.settings_enabled_help")}
        </div>
      </div>

      <div style={{ height: 12 }} />

      <div className="qw-field">
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {t("reminders.settings_schedule_label")}
        </div>
        <div className="qw-help" style={{ marginBottom: 8 }}>
          {t("reminders.settings_schedule_help", {
            max: MAX_OFFSETS,
            min: MIN_DAYS_BEFORE,
            maxDays: MAX_DAYS_BEFORE,
          })}
        </div>

        {offsets.length === 0 ? (
          <div className="qw-help" data-testid="company-reminders-empty">
            {t("reminders.settings_schedule_empty")}
          </div>
        ) : (
          <div
            className="hstack"
            style={{ flexWrap: "wrap", gap: 8 }}
            data-testid="company-reminders-chips"
          >
            {offsets.map((days) => (
              <span
                key={days}
                className="qw-badge"
                data-testid={`company-reminder-chip-${days}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: "#f1f5f9",
                  color: "#0f172a",
                  fontSize: 13,
                }}
              >
                {offsetChipLabel(days)}
                {canEdit && (
                  <button
                    type="button"
                    className="qw-btn ghost sm"
                    aria-label={t("reminders.settings_remove_offset", {
                      label: offsetChipLabel(days),
                    })}
                    data-testid={`company-reminder-remove-${days}`}
                    onClick={() => drop(days)}
                    style={{ padding: "0 4px", lineHeight: 1 }}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {canEdit && (
          <div className="hstack" style={{ gap: 8, marginTop: 12, alignItems: "flex-start" }}>
            <input
              className="qw-input"
              type="text"
              inputMode="numeric"
              value={draftOffset}
              disabled={full}
              placeholder={t("reminders.settings_add_placeholder")}
              aria-label={t("reminders.settings_add_label")}
              data-testid="company-reminder-new"
              style={{ maxWidth: 160 }}
              onChange={(e) => {
                setDraftOffset(e.target.value);
                setAddError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
            />
            <button
              type="button"
              className="qw-btn sm"
              disabled={full}
              onClick={add}
              data-testid="company-reminder-add"
            >
              {t("reminders.settings_add_action")}
            </button>
          </div>
        )}

        {full && canEdit && (
          <div className="qw-help" style={{ marginTop: 8 }} data-testid="company-reminders-full">
            {t("reminders.settings_full", { max: MAX_OFFSETS })}
          </div>
        )}
        {addError && (
          <div className="qw-error" style={{ marginTop: 8 }} data-testid="company-reminder-add-error">
            {addError}
          </div>
        )}
        {error && (
          <div className="qw-error" style={{ marginTop: 8 }} data-testid="company-reminders-error">
            {error}
          </div>
        )}
      </div>

      <div className="qw-help" style={{ marginTop: 12 }}>
        {t("reminders.settings_future_only")}
      </div>
    </div>
  );
}
